import { useSelector } from "react-redux";
import { RootState } from "../../store"; // Adjust the path to your Redux store file
import { toTitleCase } from "../commonfunctions/TitleCase";

// type Translations = Record<string, string>;
type Content = any;

interface UseTranslationReturn {
  translate: (englishText: string, titlecase?: string) => string;
  replace: (englishText: string, values: Record<string, string>) => string;
  language: string;
  notEnglish: boolean
}

/**
 * Replaces placeholders in a string with corresponding values.
 * Calls the translate function to get the translated message.
 * @param {string} englishText - The English message containing placeholders (e.g., {{key}}).
 * @param {Record<string, string>} values - An object containing key-value pairs to replace in the message.
 * @param {Function} translate - The translation function to get the translated message.
 * @returns {string} - The translated message with placeholders replaced.
 */
const replacePlaceholders = (
  englishText: string,
  values: Record<string, string>,
  translate: (text: string, titlecase?: string) => string
): string => {
  const translatedMessage = translate(englishText);

  if (!translatedMessage || typeof translatedMessage !== "string") {
    throw new Error("Invalid message format");
  }

  if (!values || typeof values !== "object") {
    throw new Error("Invalid values object");
  }
  return translatedMessage.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return values[key] !== undefined ? values[key] : `{{${key}}}`;
  });
};

const useTranslation = (content: Content): UseTranslationReturn => {
  // Get the current language from Redux
  const language = useSelector(
    (state: RootState) => state?.language?.language
  ) as string; // Explicitly cast to string

  // Translation function
  const translate = (englishText: string, titlecase?: string): string => {
    const translations = content[language] || {};
    if (titlecase) {
      return translations[englishText] ? translations[englishText] : toTitleCase(englishText, titlecase);
    } else {
      return translations[englishText] ? translations[englishText] : englishText;
    }

  };

  // Replace function
  const replace = (englishText: string, values: Record<string, string>): string => {
    return replacePlaceholders(englishText, values, translate);
  };
  const notEnglish = language !== 'english';

  return { translate, replace, language, notEnglish };
};

export default useTranslation;
